<template>
  <div class="statistics-container">
    <a-page-header title="AI调用统计报表" @back="() => $router.go(-1)">
      <template #extra>
        <a-space>
          <a-range-picker 
            v-model:value="dateRange" 
            :presets="predefinedRanges"
            @change="handleDateChange"
          />
          <a-button type="primary" @click="fetchData" :loading="loading">
            <template #icon><ReloadOutlined /></template>
            刷新数据
          </a-button>
          <a-button @click="handleManualAggregate" :loading="aggregating">
            手动汇总历史
          </a-button>
        </a-space>
      </template>
    </a-page-header>

    <div class="content-wrapper">
      <!-- 核心指标卡片 -->
      <a-row :gutter="16">
        <a-col :span="6">
          <a-card>
            <a-statistic title="总调用量" :value="summary.total_count" />
          </a-card>
        </a-col>
        <a-col :span="6">
          <a-card>
            <a-statistic 
              title="完成率" 
              :value="summary.completion_rate" 
              suffix="%" 
              :precision="2"
              :value-style="{ color: summary.completion_rate > 80 ? '#3f8600' : '#cf1322' }"
            />
          </a-card>
        </a-col>
        <a-col :span="6">
          <a-card>
            <a-statistic 
              title="反馈覆盖率" 
              :value="summary.feedback_rate" 
              suffix="%" 
              :precision="2"
            />
          </a-card>
        </a-col>
        <a-col :span="6">
          <a-card>
            <a-statistic 
              title="反馈好评率" 
              :value="summary.helpful_rate" 
              suffix="%" 
              :precision="2"
              :value-style="{ color: summary.helpful_rate > 70 ? '#3f8600' : '#cf1322' }"
            />
          </a-card>
        </a-col>
      </a-row>

      <!-- 图表区域 -->
      <a-row :gutter="16" style="margin-top: 16px">
        <a-col :span="16">
          <a-card title="调用量趋势">
            <div ref="trendChartRef" style="height: 400px"></div>
          </a-card>
        </a-col>
        <a-col :span="8">
          <a-card title="状态分布">
            <div ref="statusChartRef" style="height: 400px"></div>
          </a-card>
        </a-col>
      </a-row>

      <a-row :gutter="16" style="margin-top: 16px">
        <a-col :span="12">
          <a-card title="反馈分析">
            <div ref="feedbackChartRef" style="height: 350px"></div>
          </a-card>
        </a-col>
        <a-col :span="12">
          <a-card title="响应耗时趋势 (秒)">
            <div ref="durationChartRef" style="height: 350px"></div>
          </a-card>
        </a-col>
      </a-row>
    </div>
  </div>
</template>

<script>
import { ref, reactive, onMounted, onUnmounted, nextTick } from 'vue';
import { message, Modal } from 'ant-design-vue';
import { ReloadOutlined } from '@ant-design/icons-vue';
import * as echarts from 'echarts';
import dayjs from 'dayjs';
import { getExecutionPlanStats, aggregateExecutionPlanStats } from '../api/executionPlanStats';

export default {
  name: 'ExecutionPlanStatistics',
  components: {
    ReloadOutlined
  },
  setup() {
    const loading = ref(false);
    const aggregating = ref(false);
    const dateRange = ref([dayjs().subtract(29, 'day'), dayjs()]);
    
    const summary = reactive({
      total_count: 0,
      completion_rate: 0,
      feedback_rate: 0,
      helpful_rate: 0
    });

    const trendChartRef = ref(null);
    const statusChartRef = ref(null);
    const feedbackChartRef = ref(null);
    const durationChartRef = ref(null);

    let trendChart = null;
    let statusChart = null;
    let feedbackChart = null;
    let durationChart = null;

    const predefinedRanges = [
      { label: '最近7天', value: [dayjs().subtract(6, 'day'), dayjs()] },
      { label: '最近30天', value: [dayjs().subtract(29, 'day'), dayjs()] },
      { label: '本月', value: [dayjs().startOf('month'), dayjs()] }
    ];

    const initCharts = () => {
      trendChart = echarts.init(trendChartRef.value);
      statusChart = echarts.init(statusChartRef.value);
      feedbackChart = echarts.init(feedbackChartRef.value);
      durationChart = echarts.init(durationChartRef.value);

      window.addEventListener('resize', handleResize);
    };

    const handleResize = () => {
      trendChart?.resize();
      statusChart?.resize();
      feedbackChart?.resize();
      durationChart?.resize();
    };

    const fetchData = async () => {
      if (!dateRange.value || dateRange.value.length !== 2) return;
      
      loading.value = true;
      try {
        const startDate = dateRange.value[0].format('YYYY-MM-DD');
        const endDate = dateRange.value[1].format('YYYY-MM-DD');
        
        const res = await getExecutionPlanStats({ startDate, endDate });
        if (res.code === 200) {
          processData(res.data);
        }
      } catch (error) {
        message.error('获取统计数据失败');
      } finally {
        loading.value = false;
      }
    };

    const processData = (data) => {
      // 合并历史和今天的数据
      const allData = [...data.history];
      if (data.today) {
        allData.push(data.today);
      }

      // 计算核心指标
      let total = 0, completed = 0, failed = 0, cancelled = 0, feedback = 0, helpful = 0;
      allData.forEach(item => {
        total += Number(item.total_count || 0);
        completed += Number(item.completed_count || 0);
        failed += Number(item.failed_count || 0);
        cancelled += Number(item.cancelled_count || 0);
        feedback += Number(item.feedback_count || 0);
        helpful += Number(item.helpful_count || 0);
      });

      summary.total_count = total;
      summary.completion_rate = total > 0 ? (completed / total) * 100 : 0;
      summary.feedback_rate = completed > 0 ? (feedback / completed) * 100 : 0;
      summary.helpful_rate = feedback > 0 ? (helpful / feedback) * 100 : 0;

      // 渲染趋势图
      renderTrendChart(allData);
      // 渲染分布图
      renderStatusChart(completed, failed, cancelled, total - completed - failed - cancelled);
      // 渲染反馈图
      renderFeedbackChart(helpful, feedback - helpful);
      // 渲染耗时图
      renderDurationChart(allData);
    };

    const renderTrendChart = (data) => {
      const option = {
        tooltip: { trigger: 'axis' },
        legend: { data: ['总调用量', '已完成'] },
        xAxis: { type: 'category', data: data.map(item => dayjs(item.stats_date).format('MM-DD')) },
        yAxis: { type: 'value' },
        series: [
          { name: '总调用量', type: 'line', data: data.map(item => item.total_count), smooth: true },
          { name: '已完成', type: 'line', data: data.map(item => item.completed_count), smooth: true }
        ]
      };
      trendChart.setOption(option);
    };

    const renderStatusChart = (completed, failed, cancelled, other) => {
      const option = {
        tooltip: { trigger: 'item' },
        series: [{
          type: 'pie',
          radius: ['40%', '70%'],
          data: [
            { value: completed, name: '已完成' },
            { value: failed, name: '失败' },
            { value: cancelled, name: '已取消' },
            { value: other > 0 ? other : 0, name: '执行中/待处理' }
          ]
        }]
      };
      statusChart.setOption(option);
    };

    const renderFeedbackChart = (helpful, unhelpful) => {
      const option = {
        tooltip: { trigger: 'item' },
        legend: { bottom: '0' },
        series: [{
          type: 'pie',
          radius: '50%',
          data: [
            { value: helpful, name: '有帮助' },
            { value: unhelpful, name: '没帮助' }
          ]
        }]
      };
      feedbackChart.setOption(option);
    };

    const renderDurationChart = (data) => {
      const option = {
        tooltip: { trigger: 'axis' },
        xAxis: { type: 'category', data: data.map(item => dayjs(item.stats_date).format('MM-DD')) },
        yAxis: { type: 'value' },
        series: [{
          name: '平均耗时',
          type: 'bar',
          data: data.map(item => parseFloat(item.avg_duration || 0).toFixed(1)),
          itemStyle: { color: '#1890ff' }
        }]
      };
      durationChart.setOption(option);
    };

    const handleDateChange = () => {
      fetchData();
    };

    const handleManualAggregate = () => {
      Modal.confirm({
        title: '手动汇总历史数据',
        content: '这将重新计算所选时间范围内的每日统计数据（不包含今天）。确定继续吗？',
        onOk: async () => {
          aggregating.value = true;
          try {
            const startDate = dateRange.value[0].format('YYYY-MM-DD');
            const endDate = dayjs().subtract(1, 'day').format('YYYY-MM-DD');
            await aggregateExecutionPlanStats({ startDate, endDate });
            message.success('汇总任务已启动');
            fetchData();
          } catch (error) {
            message.error('启动汇总失败');
          } finally {
            aggregating.value = false;
          }
        }
      });
    };

    onMounted(() => {
      nextTick(() => {
        initCharts();
        fetchData();
      });
    });

    onUnmounted(() => {
      window.removeEventListener('resize', handleResize);
      trendChart?.dispose();
      statusChart?.resize();
      feedbackChart?.dispose();
      durationChart?.dispose();
    });

    return {
      loading,
      aggregating,
      dateRange,
      predefinedRanges,
      summary,
      trendChartRef,
      statusChartRef,
      feedbackChartRef,
      durationChartRef,
      fetchData,
      handleDateChange,
      handleManualAggregate
    };
  }
};
</script>

<style scoped>
.statistics-container {
  background: #f0f2f5;
  min-height: 100vh;
}
.content-wrapper {
  padding: 16px 24px;
}
.ant-card {
  border-radius: 8px;
}
</style>
